import { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { getUnreadCounts } from '../../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Dashboard,
  ContactMail,
  Science,
  Settings,
  Logout,
  Menu as MenuIcon,
  ChevronLeft,
  People,
  RequestQuote,
} from '@mui/icons-material';

/* ── Design tokens (mirrors CSS variables in App.css) ──────────── */
const PRIMARY = '#6C98AD';
// eslint-disable-next-line no-unused-vars
const PRIMARY_HOVER = '#5a8499';
const PRIMARY_LIGHT = 'rgba(108, 152, 173, 0.18)';
// eslint-disable-next-line no-unused-vars
const PRIMARY_SUBTLE = 'rgba(108, 152, 173, 0.08)';
const SECONDARY = '#24272a';
const BG_GRADIENT = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
const CARD_SHADOW = '0 4px 20px rgba(108, 152, 173, 0.15)';
const FONT_HEADING = "'Avenir Next LT Pro', sans-serif";
const FONT_BODY = "'Avenir Next LT Pro', sans-serif";
const RADIUS = '1rem';
const TRANSITION = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

/* ── Sidebar light-theme tokens ─────────────────────────────── */
const SIDEBAR_BG = '#ffffff';
const SIDEBAR_SURFACE = 'rgba(108, 152, 173, 0.06)';
const SIDEBAR_BORDER = 'rgba(108, 152, 173, 0.12)';
const SIDEBAR_TEXT = '#666666';
const SIDEBAR_TEXT_ACTIVE = SECONDARY;

const DRAWER_WIDTH = 270;

const getNavItems = (unreadCounts) => [
  { label: 'Dashboard', icon: <Dashboard />, path: '/admin' },
  { label: 'Contacts', icon: <ContactMail />, path: '/admin/contacts', badge: unreadCounts.unreadContacts },
  { label: 'Request for Quote (RFQ)', icon: <Science />, path: '/admin/rfqs', badge: unreadCounts.unreadRfqs, resetsToList: true },
  { label: 'Quotes', icon: <RequestQuote />, path: '/admin/quotes', resetsToList: true },
  { label: 'ClearBox Config', icon: <Settings />, path: '/admin/clearbox-config' },
  { label: 'Accounts', icon: <People />, path: '/admin/accounts' },
];

export default function AdminLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [unreadCounts, setUnreadCounts] = useState({ unreadContacts: 0, unreadRfqs: 0 });
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const fetchUnreadCounts = useCallback(async () => {
    try {
      const counts = await getUnreadCounts();
      setUnreadCounts(counts);
    } catch (err) {
      console.error('Failed to fetch unread counts:', err);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCounts();
    // Refresh counts every 30 seconds
    const interval = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCounts]);

  // Refresh counts when navigating
  useEffect(() => {
    fetchUnreadCounts();
  }, [location.pathname, fetchUnreadCounts]);

  const navItems = getNavItems(unreadCounts);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: FONT_BODY }}>
      {/* ── Sidebar header ─────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2.5,
          py: 2.5,
          minHeight: 70,
          position: 'relative',
        }}
      >
        <img
          src={process.env.PUBLIC_URL + '/logos/ta_bg_less_light.svg'}
          alt="Tailored Alloys"
          height="32"
          style={{ display: 'block' }}
        />
        {isMobile && (
          <IconButton
            onClick={() => setDrawerOpen(false)}
            sx={{ color: SECONDARY, position: 'absolute', right: 8 }}
          >
            <ChevronLeft />
          </IconButton>
        )}
      </Box>

      <Divider sx={{ borderColor: SIDEBAR_BORDER }} />

      {/* ── Navigation ─────────────────────────────────────────── */}
      <List sx={{ flex: 1, px: 1.5, pt: 2 }}>
        {navItems.map((item) => {
          const isActive =
            item.path === '/admin'
              ? location.pathname === '/admin'
              : location.pathname.startsWith(item.path);
          return (
            <ListItemButton
              key={item.path}
              component={RouterLink}
              to={item.path}
              state={item.resetsToList ? { resetToList: true } : undefined}
              onClick={() => isMobile && setDrawerOpen(false)}
              sx={{
                borderRadius: RADIUS,
                mb: 0.5,
                py: 1.1,
                transition: TRANSITION,
                backgroundColor: isActive ? PRIMARY_LIGHT : 'transparent',
                color: isActive ? SIDEBAR_TEXT_ACTIVE : SIDEBAR_TEXT,
                '&:hover': {
                  backgroundColor: isActive ? PRIMARY_LIGHT : SIDEBAR_SURFACE,
                  transform: 'translateX(4px)',
                  color: SIDEBAR_TEXT_ACTIVE,
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 38 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontFamily: FONT_BODY,
                  fontWeight: isActive ? 600 : 400,
                  fontSize: '0.92rem',
                }}
              />
              {item.badge > 0 && (
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: 0.75,
                    borderRadius: '10px',
                    background: `linear-gradient(135deg, ${PRIMARY} 0%, #8eb4c7 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: isActive ? 1 : 0,
                    boxShadow: '0 2px 8px rgba(108, 152, 173, 0.4)',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: '#fff',
                      fontFamily: FONT_HEADING,
                      lineHeight: 1,
                    }}
                  >
                    {item.badge > 99 ? '99+' : item.badge}
                  </Typography>
                </Box>
              )}
              {isActive && (
                <Box
                  sx={{
                    width: 4,
                    height: 20,
                    borderRadius: 2,
                    background: `linear-gradient(180deg, ${PRIMARY} 0%, #8eb4c7 100%)`,
                    ml: 1,
                  }}
                />
              )}
            </ListItemButton>
          );
        })}
      </List>

      <Divider sx={{ borderColor: SIDEBAR_BORDER }} />

      {/* ── User card ──────────────────────────────────────────── */}
      <Box
        sx={{
          p: 2,
          mx: 1.5,
          my: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderRadius: RADIUS,
          background: SIDEBAR_SURFACE,
          border: `1px solid ${SIDEBAR_BORDER}`,
          transition: TRANSITION,
        }}
      >
        <Avatar
          sx={{
            width: 34,
            height: 34,
            background: `linear-gradient(135deg, ${PRIMARY} 0%, #8eb4c7 100%)`,
            fontSize: '0.85rem',
            fontFamily: FONT_HEADING,
            fontWeight: 700,
            boxShadow: '0 4px 12px rgba(108, 152, 173, 0.3)',
          }}
        >
          {admin?.name?.[0]?.toUpperCase() || 'A'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: 600, fontSize: '0.82rem', color: SIDEBAR_TEXT_ACTIVE, fontFamily: FONT_BODY }}
          >
            {admin?.name || admin?.email || 'Admin'}
          </Typography>
          <Typography
            variant="caption"
            noWrap
            sx={{ color: PRIMARY, fontSize: '0.72rem' }}
          >
            Administrator
          </Typography>
        </Box>
        <Tooltip title="Sign out">
          <IconButton
            size="small"
            onClick={handleLogout}
            sx={{
              color: SIDEBAR_TEXT,
              transition: TRANSITION,
              '&:hover': { color: '#e53935', transform: 'translateY(-2px)' },
            }}
          >
            <Logout fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: BG_GRADIENT, fontFamily: FONT_BODY }}>
      {/* Sidebar */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            border: 'none',
            background: SIDEBAR_BG,
            boxShadow: '4px 0 24px rgba(108, 152, 173, 0.15)',
            borderRight: `1px solid ${SIDEBAR_BORDER}`,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Mobile top bar */}
        {isMobile && (
          <AppBar
            position="sticky"
            elevation={0}
            sx={{
              background: `linear-gradient(135deg, ${SECONDARY} 0%, #2d3236 100%)`,
              color: '#fff',
              boxShadow: CARD_SHADOW,
            }}
          >
            <Toolbar>
              <IconButton edge="start" onClick={() => setDrawerOpen(true)} sx={{ mr: 2, color: 'inherit' }}>
                <MenuIcon />
              </IconButton>
              <img
                src={process.env.PUBLIC_URL + '/TALOGOWEBSITE.webp'}
                alt="Tailored Alloys"
                height="28"
              />
            </Toolbar>
          </AppBar>
        )}

        {/* Page Content */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 2, sm: 3, md: 4 },
            maxWidth: 1400,
            width: '100%',
            mx: 'auto',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
